import * as THREE from 'three';
import { DamageRender } from "../helpers/damagerender";
import { DataCache } from "../helpers/datacache";
import { OrthoSprite } from "../helpers/orthosprite";
import { SkelAnimPawn } from "./skelanimpawn";
import { SkeletonAnimator } from "../animators/skeletonanimator";
import { TextureManager } from "../datamanagers/texturemanager";
import { AnimationData } from "../loaders/zmo";
import { GDM } from "../helpers/datamanager";
import { Mesh } from "../loaders/zms";
import { MOTION_TABLE, DummyIndex, AVTANI } from "../constants";
import { VISEQUIPIDX, ITEMTYPE, WEAPON_DATA } from "../gamedata/inventorydata";
import { EffectManager } from "../effects/effectmanager";
import { dataUrl } from "../util/path";

// Toggle for player torch light
const PLAYER_TORCH_ENABLED = true;
const DRIVING_AVATAR_HEIGHT = 0.45;
const CASTLE_GEAR_AVATAR_HEIGHT = 3.32;
const VEHICLE_RIDER_FORWARD_OFFSET = -0.18;
const CASTLE_GEAR_STOP_MOTION = "3DDATA/MOTION/AVATAR/CASTLEGEAR01_STOP.ZMO";
const CASTLE_GEAR_WALK_MOTION = "3DDATA/MOTION/AVATAR/CASTLEGEAR01_WALK.ZMO";
// CART01.ZMD (56 bones) is bone-compatible with these clips (max channel
// index 55); the cart's stat parts are skinned meshes, same as Castle Gear.
const CART_STOP_MOTION = "3DDATA/MOTION/AVATAR/CAR_STOP_01.ZMO";
const CART_WALK_MOTION = "3DDATA/MOTION/AVATAR/CAR_MOVE_01.ZMO";
const PAT_VEHICLE_SKELETONS: Record<'castleGear' | 'cart', { skelName: string; stop: string; walk: string }> = {
    castleGear: { skelName: 'castle_gear_skel', stop: CASTLE_GEAR_STOP_MOTION, walk: CASTLE_GEAR_WALK_MOTION },
    cart: { skelName: 'cart_skel', stop: CART_STOP_MOTION, walk: CART_WALK_MOTION },
};
export type PatVehicleKind = 'castleGear' | 'cart' | null;

// LIST_PAT.STB frame/core subtypes (see PAT_SUBTYPE_TO_SLOT in inventorydata.ts).
export function getPatVehicleKind(subtype: number | undefined): PatVehicleKind {
    if (subtype === 512) return 'castleGear';
    if (subtype === 511) return 'cart';
    return null;
}
// LIST_PAT.ZSC references CastleGear15 meshes for these KT-Mech parts, but
// the shipped game-data folder contains only CastleGear02 meshes. Use its
// matching part roles until the KT-Mech meshes are available.
const PAT_VISUAL_FALLBACK: Record<number, number> = {
    243: 232, // KT-Mech Legs -> Runihol Leg
    244: 32,  // KT-Mech Core -> Reinforced Stock Core
    245: 331, // KT-Mech Arms -> Punch Arms
};

export class CharPawn extends SkelAnimPawn {
    static motionFileCache: any;

    static preloadPatParts(itemNos: number[], vehicleKind: PatVehicleKind) {
        const vehicle = vehicleKind ? PAT_VEHICLE_SKELETONS[vehicleKind] : null;
        const resources = vehicle ? [vehicle.skelName, "itm_pat"] : ["itm_pat"];

        GDM.get(...resources, (...loaded: any[]) => {
            const patData = loaded[loaded.length - 1];
            if (!patData) return;

            // Prime the equipped meshes while the inventory snapshot is being
            // processed. ModelListManager coalesces concurrent requests and
            // serves these geometries synchronously from its cache when the
            // player later enters the driving stance.
            for (const itemNo of itemNos) {
                if (!itemNo) continue;
                const model = patData.data.models[PAT_VISUAL_FALLBACK[itemNo] || itemNo];
                if (!model) continue;
                for (const part of model.parts) {
                    patData._createMesh(part.meshIdx, () => undefined);
                }
            }
        });

        if (vehicle) {
            CharPawn.motionFileCache.get(vehicle.stop, () => undefined);
        }
    }

    owner: any;
    gender: number;
    nameTag: any;
    chatBubble: any;
    modelParts: any[];
    weaponMotionType: number;
    pendingResources: number;
    totalResources: number;
    readyCallbacks: Function[];
    isInitializing: boolean;
    visParts: any;
    avatarRoot: THREE.Object3D;
    patRoot: THREE.Object3D;
    isCastleGearDriving = false;
    private patVehicleKind: PatVehicleKind = null;
    private patSkeleton: THREE.Skeleton | null = null;
    private patAnimation: SkeletonAnimator | null = null;
    private patAnimationKind: 'stop' | 'walk' | null = null;
    private patMotionRequest = 0;
    private patMoving = false;

    constructor(go?: any) {
        super();
        this.avatarRoot = new THREE.Object3D();
        this.rootObj.add(this.avatarRoot);
        this.patRoot = new THREE.Object3D();
        this.rootObj.add(this.patRoot);
        this.gender = -1;
        this.nameTag = null;
        this.chatBubble = null;
        this.modelParts = [];
        this.weaponMotionType = 1;
        this.pendingResources = 0;
        this.totalResources = 0;
        this.readyCallbacks = [];
        this.isInitializing = false;
        if (go) {
            this.owner = go;
            this.rootObj.name = "CHAR_" + "????";

            // Prevent ready callbacks during initialization
            this.isInitializing = true;

            this.setGender(go.gender, () => {
                for (let j = 0; j < go.visParts.length; j++) {
                    this.setModelPart(
                        j,
                        go.visParts[j].itemNo,
                        go.visParts[j].refineGrade || 0,
                        undefined,
                        go.visParts[j].gemOption1 || 0
                    );
                }

                // Initialization complete, allow ready callbacks now
                this.isInitializing = false;
                this._checkReady();
            });
            // var itemNr = 227; // 231;
            // var refineGrade = 0;
            // setInterval(() => {
            //     this.setModelPart(AVTBODYPART.Back, itemNr, refineGrade);
            //     refineGrade = (refineGrade + 1) % 10;
            // }, 1000);
            go.on("damage", (amount: any) => {
                this.newDamage(amount);
            });
            this.createNamePlate();

            // Add torch light to local player
            if (PLAYER_TORCH_ENABLED && go.type === 'local') {
                const torchLight = new THREE.PointLight(0xffaa44, 1.5, 50);
                torchLight.position.set(0, 0, 10); // Slightly above character
                this.avatarRoot.add(torchLight);
            }
        }
    }

    whenReady(callback: Function) {
        if (this.pendingResources === 0 && !this.isInitializing) {
            // Already ready, call immediately
            callback();
        } else {
            // Wait for resources to load
            this.readyCallbacks.push(callback);
        }
    }

    beginLoading() {
        this.isInitializing = true;
    }

    endLoading() {
        this.isInitializing = false;
        this._checkReady();
    }

    _checkReady() {
        // Don't fire ready callbacks during initialization
        if (this.isInitializing) {
            return;
        }

        if (this.pendingResources === 0 && this.readyCallbacks.length > 0) {
            const callbacks = this.readyCallbacks.slice();
            this.readyCallbacks = [];
            for (let i = 0; i < callbacks.length; i++) {
                callbacks[i]();
            }
        }
    }

    createNamePlate() {
        const texture = ui.createNamePlate(this.owner);
        // Recreate the sprite with new texture
        let material = new THREE.SpriteMaterial({
            map: texture,
            color: 0xffffff,
        });
        material.depthWrite = false;
        let sprite = new OrthoSprite(material);
        sprite.position.set(0, 0, 2.0);
        sprite.scale.set(texture.image.width, texture.image.height, 1);
        sprite.offset.set(0, -24, 0);
        this.avatarRoot.add(sprite);
        this.nameTag = sprite;
    }

    showChatBubble(message: any) {
        // Hide existing bubble
        if (this.chatBubble) {
            this.avatarRoot.remove(this.chatBubble);
            this.chatBubble = null;
        }
        // Load the chat bubble texture as PNG
        const loader = new THREE.TextureLoader();
        loader.load(
            dataUrl("3DDATA/CONTROL/RES/CHATBOX02.PNG"),
            (texture: any) => {
                // Create canvas to composite background and text
                let canvas = document.createElement("canvas");
                let ctx = canvas.getContext("2d");
                canvas.width = texture.image.width;
                canvas.height = texture.image.height;
                // Calculate text layout first
                ctx.font = "16px Arial";
                ctx.fillStyle = "black";
                ctx.textAlign = "center";
                const maxWidth = canvas.width * 0.9; // 90% of canvas width
                const lineHeight = 20;
                const words = message.split(" ");
                let lines = [];
                let currentLine = "";
                for (let i = 0; i < words.length; i++) {
                    const testLine =
                        currentLine + (currentLine ? " " : "") + words[i];
                    const metrics = ctx.measureText(testLine);
                    if (metrics.width > maxWidth && currentLine) {
                        lines.push(currentLine);
                        currentLine = words[i];
                    } else {
                        currentLine = testLine;
                    }
                }
                lines.push(currentLine);
                // Limit to 3 lines
                if (lines.length > 3) {
                    lines = lines.slice(0, 3);
                    let lastLine = lines[lines.length - 1];
                    while (
                        ctx.measureText(lastLine + "...").width > maxWidth &&
                        lastLine.length > 0
                    ) {
                        lastLine = lastLine.slice(0, -1);
                    }
                    lines[lines.length - 1] = lastLine + "...";
                }
                // Calculate text dimensions
                const textWidth =
                    Math.max(
                        ...lines.map((line) => ctx.measureText(line).width)
                    ) + 20; // padding
                const textHeight = lines.length * lineHeight + 20; // padding
                // Create new canvas sized to text
                const newCanvas = document.createElement("canvas");
                newCanvas.width = textWidth;
                newCanvas.height = textHeight;
                const newCtx = newCanvas.getContext("2d");
                // Draw scaled background image
                newCtx.drawImage(
                    texture.image,
                    0,
                    0,
                    texture.image.width,
                    texture.image.height,
                    0,
                    0,
                    textWidth,
                    textHeight
                );
                // Draw text
                newCtx.font = "16px Arial";
                newCtx.fillStyle = "black";
                newCtx.textAlign = "center";
                let startY;
                if (lines.length === 1) {
                    startY = textHeight / 2 - 5; // Position higher for single line
                } else {
                    startY =
                        (textHeight - lines.length * lineHeight) / 2 +
                        lineHeight / 2; // Center the block
                }
                for (let j = 0; j < lines.length; j++) {
                    newCtx.fillText(
                        lines[j],
                        textWidth / 2,
                        startY + j * lineHeight
                    );
                }
                // Use the new canvas
                canvas = newCanvas;
                ctx = newCtx;
                // Create texture from canvas
                const finalTexture = new THREE.Texture(canvas);
                finalTexture.needsUpdate = true;
                // Create sprite with composite texture
                let material = new THREE.SpriteMaterial({
                    map: finalTexture,
                    transparent: true,
                });
                material.depthWrite = false;
                const sprite = new OrthoSprite(material);
                sprite.position.set(0, 0, 2.5); // Above name tag
                sprite.scale.set(canvas.width, canvas.height, 1);
                this.avatarRoot.add(sprite);
                this.chatBubble = sprite;
                // Hide after 5 seconds
                setTimeout(() => {
                    if (this.chatBubble) {
                        this.avatarRoot.remove(this.chatBubble);
                        this.chatBubble = null;
                    }
                }, 5000);
            }
        );
    }

    setGender(genderIdx: any, callback: any) {
        // Dont waste time doing nothing
        if (this.gender === genderIdx) {
            if (callback) {
                callback();
            }
            return;
        }
        this.gender = genderIdx;
        const skelName = GENDERSKELNAMES[genderIdx];
        if (!skelName) {
            throw new Error("Invalid gender specified (" + genderIdx + ")");
        }
        this.pendingResources++;
        this.totalResources++;
        GDM.get(skelName, (skelData: any) => {
            this._setSkeleton(skelData);
            // Reload items
            for (let i = 0; i < this.modelParts.length; ++i) {
                if (this.modelParts[i]) {
                    this.setModelPart(
                        i,
                        this.modelParts[i].id,
                        this.modelParts[i].refine || 0,
                        undefined,
                        this.modelParts[i].gem || 0
                    );
                }
            }
            this.pendingResources--;
            this._checkReady();
            if (callback) {
                callback();
            }
        });
    }

    _setModelPart(
        modelList: any,
        partIdx: any,
        modelIdx: any,
        refine: any,
        bindBone: any,
        bindDummy: any,
        callback?: Function,
        gem: number = 0
    ) {
        // Remove old model
        if (this.modelParts[partIdx]) {
            for (let i = 0; i < this.modelParts[partIdx].meshes.length; ++i) {
                const part = this.modelParts[partIdx].meshes[i];
                part.parent.remove(part);
            }
            this.modelParts[partIdx].gemEffects.forEach((effect: any) => effect.stop());
        }
        const entry = {
            id: modelIdx,
            refine: refine,
            gem: gem,
            meshes: [] as any[],
            // Meshes by ZSC part index -- meshes[] is in load-completion order.
            partMeshes: [] as any[],
            gemEffects: [] as any[],
        };
        this.modelParts[partIdx] = entry;
        // Add new model
        const model = modelList.data.models[modelIdx];
        if (!model) {
            if (modelIdx !== 0) {
                console.warn(
                    "Tried to set avatar part to invalid item (" +
                        partIdx +
                        ", " +
                        modelIdx +
                        ", " +
                        bindBone +
                        ")"
                );
            }
            if (callback) {
                callback();
            }
            return;
        }

        // Track how many mesh parts we're loading
        let pendingMeshes = model.parts.length;
        if (pendingMeshes === 0 && callback) {
            callback();
            return;
        }

        for (let j = 0; j < model.parts.length; ++j) {
            ((part, j) => {
                let material = modelList._createMaterial(part.materialIdx);
                const meshPath = modelList.data.meshes[part.meshIdx];
                Mesh.load(meshPath, (geometry: any) => {
                    let mesh;
                    if (part.boneIndex !== undefined) {
                        bindBone = part.boneIndex;
                    }
                    if (part.dummyIndex !== undefined) {
                        bindDummy = part.dummyIndex;
                    }
                    if (bindBone === undefined && bindDummy === undefined) {
                        mesh = new THREE.SkinnedMesh(geometry, material);
                        // Add the root bone to the mesh so bone hierarchy is accessible
                        mesh.add(this.skel.bones[0]);
                        mesh.bind(this.skel);
                        this.avatarRoot.add(mesh);
                        this._addBoundsGeometry(mesh);
                    } else {
                        mesh = new THREE.Mesh(geometry, material);
                        if (bindBone !== undefined) {
                            this.skel.bones[bindBone].add(mesh);
                        } else if (bindDummy !== undefined) {
                            this.skel.dummies[bindDummy].add(mesh);
                        } else {
                            console.warn("Loaded part with no bind location");
                        }
                    }
                    // Make refined items glow and translucent
                    if (refine > 0) {
                        // this.applyRefineGlow(mesh, refine);
                        // mesh.material = mesh.material.clone();
                        const intensity = refine * 0.1; // Scales to ~0.5 at refine 9
                        mesh.material.emissive = new THREE.Color(
                            intensity,
                            intensity,
                            intensity
                        );
                        mesh.material.transparent = true;
                        let opacity = 1 - refine * 0.015;
                        mesh.material.opacity = Math.max(opacity, 0.1); // Prevent fully transparent
                        // console.log(
                        //     `Refinement ${refine}, intensity: ${intensity}, opacity: ${opacity}`
                        // );
                    }
                    this.modelParts[partIdx].meshes.push(mesh);
                    entry.partMeshes[j] = mesh;

                    // Check if all meshes are loaded
                    pendingMeshes--;
                    if (pendingMeshes === 0) {
                        if (gem && this.modelParts[partIdx] === entry) {
                            this._attachGemEffects(entry, model, partIdx);
                        }
                        if (callback) callback();
                    }
                });
            })(model.parts[j], j);
        }
    }

    applyRefineGlow(mesh: any, refine: any, glowColor: any) {
        if (!mesh || !mesh.material || refine <= 0) return;
        const srcMat = mesh.material;
        let baseMat = srcMat.clone();
        if (!("emissive" in baseMat)) {
            baseMat = new THREE.MeshStandardMaterial({ color: 0xffffff });
        }
        mesh.material = baseMat;
        let t = Math.min(Math.max(refine, 1), 9) / 9; // [0..1]
        const baseColor = new THREE.Color(glowColor || 0xffffff);
        // 1) Basis emissive: extreem laag en lineair
        baseMat.emissive = baseColor.clone();
        baseMat.emissiveIntensity = 0.00000015 * t; // nog lager dan voorheen
        baseMat.transparent = true;
        let opacity = Math.max(1 - refine * 0.005, 0.8);
        baseMat.opacity = opacity;
        baseMat.blending = THREE.NormalBlending;
        baseMat.depthWrite = true;
        // 2) Glow shell (donkere kleur voor headroom)
        const existingGlow = mesh._glowShell;
        const glowMat = existingGlow
            ? existingGlow.material
            : new THREE.MeshBasicMaterial();
        const headroomColor = baseColor.clone().multiplyScalar(0.0025); // dempt bronkleur
        glowMat.color = headroomColor;
        glowMat.transparent = true;
        glowMat.depthWrite = false;
        // // Niet-lineaire curves + thresholds
        // var curve = Math.pow(t, 3.0); // traag oplopend
        // var lowCap = 0.0; // ondergrens
        // var midCap = 0.006; // max bij CustomBlending
        // var highCap = 0.015; // max bij Additive
        // // Schakel blending naar gelang refine
        // if (refine <= 3) {
        //     // Heel subtiel, of zelfs uit bij lage refine
        //     glowMat.opacity = Math.max(lowCap, 0.002 * curve); // ~0 bij +1
        //     glowMat.blending = THREE.CustomBlending;
        //     glowMat.blendEquation = THREE.AddEquation;
        //     glowMat.blendSrc = THREE.SrcAlphaFactor;
        //     glowMat.blendDst = THREE.OneMinusSrcAlphaFactor; // lineaire mix i.p.v. brightening
        // } else if (refine <= 6) {
        //     glowMat.opacity = Math.min(0.004 + 0.006 * curve, midCap);
        //     glowMat.blending = THREE.CustomBlending;
        //     glowMat.blendEquation = THREE.AddEquation;
        //     glowMat.blendSrc = THREE.SrcAlphaFactor;
        //     glowMat.blendDst = THREE.OneMinusSrcAlphaFactor;
        // } else {
        //     // Pas vanaf hoge refine additive en nog steeds laag cap
        //     glowMat.opacity = Math.min(0.006 + 0.012 * curve, highCap);
        //     glowMat.blending = THREE.AdditiveBlending;
        // }
        // if (!existingGlow) {
        //     var glowMesh = new THREE.Mesh(mesh.geometry, glowMat);
        //     glowMesh.matrixAutoUpdate = false;
        //     glowMesh.frustumCulled = false;
        //     glowMesh.onBeforeRender = function () {
        //         glowMesh.matrix.copy(mesh.matrixWorld);
        //         glowMesh.matrixWorldNeedsUpdate = true;
        //     };
        //     mesh.add(glowMesh);
        //     mesh._glowShell = glowMesh;
        // } else {
        //     existingGlow.material = glowMat;
        // }
        if (typeof console !== "undefined") {
            console.log(
                "Refine %d | t=%.2f baseEmi=%.3f baseOpacity=%.3f shellOpacity=%.4f blend=%s color=#%s",
                refine,
                t,
                baseMat.emissiveIntensity,
                baseMat.opacity,
                glowMat.opacity,
                glowMat.blending === THREE.AdditiveBlending
                    ? "Additive"
                    : "Custom(alpha)",
                baseColor.getHexString()
            );
        }
    }

    // applyRefineGlow(
    //     mesh,
    //     refine,
    //     glowColor /* hex|number|string|THREE.Color */
    // ) {
    //     if (!mesh || !mesh.material || refine <= 0) return;
    //     // 1) Clone basis-materiaal en zorg voor emissive support
    //     var srcMat = mesh.material;
    //     var baseMat = srcMat.clone();
    //     if (!("emissive" in baseMat)) {
    //         baseMat = new THREE.MeshStandardMaterial({ color: 0xffffff });
    //     }
    //     mesh.material = baseMat;
    //     // 2) Basis emissive kleur en intensiteit (niet-additive, zeer bescheiden)
    //     var t = Math.min(Math.max(refine, 1), 9) / 9; // [0..1]
    //     var baseColor = new THREE.Color(glowColor || 0xffd700);
    //     baseMat.emissive = baseColor.clone();
    //     // Houd basis-intensiteit laag, lineair met t
    //     baseMat.emissiveIntensity = 0.05 * t; // pas aan indien nodig
    //     // 3) Transparantie en blending: normaal houden voor basismateriaal
    //     baseMat.transparent = true;
    //     var opacity = Math.max(1 - refine * 0.015, 0.1);
    //     baseMat.opacity = opacity;
    //     baseMat.blending = THREE.NormalBlending;
    //     baseMat.depthWrite = true;
    //     // 4) Optionele additive "glow shell" voor halo
    //     //    – extreem lage opacity + cubic curve voorkomt te fel bij +1
    //     var existingGlow = mesh._glowShell;
    //     var glowMat = existingGlow
    //         ? existingGlow.material
    //         : new THREE.MeshBasicMaterial();
    //     glowMat.color = baseColor.clone();
    //     glowMat.transparent = true;
    //     glowMat.blending = THREE.AdditiveBlending;
    //     glowMat.depthWrite = false;
    //     var curve = Math.pow(t, 3.0); // sublineaire groei
    //     glowMat.opacity = 0.02 * curve; // max 0.02 bij +9, ~0 bij +1
    //     if (!existingGlow) {
    //         var glowMesh = new THREE.Mesh(mesh.geometry, glowMat);
    //         glowMesh.matrixAutoUpdate = false;
    //         glowMesh.frustumCulled = false;
    //         // Zorg dat de glow samen met het basismesh beweegt
    //         glowMesh.onBeforeRender = function (
    //             renderer,
    //             scene,
    //             camera,
    //             geometry,
    //             material,
    //             group
    //         ) {
    //             glowMesh.matrix.copy(mesh.matrixWorld);
    //             glowMesh.matrixWorldNeedsUpdate = true;
    //         };
    //         mesh.add(glowMesh);
    //         mesh._glowShell = glowMesh;
    //     } else {
    //         existingGlow.material = glowMat;
    //     }
    //     // 5) Debug
    //     if (typeof console !== "undefined") {
    //         console.log(
    //             "Refine %d | t=%.2f baseEmi=%.3f baseOpacity=%.3f glowOpacity=%.4f color=#%s",
    //             refine,
    //             t,
    //             baseMat.emissiveIntensity,
    //             baseMat.opacity,
    //             glowMat.opacity,
    //             baseColor.getHexString()
    //         );
    //     }
    //
    // }

    // applyRefineGlow(
    //     mesh,
    //     refine,
    //     glowColor = 0xffffff
    // ) {
    //     if (refine <= 0) return;
    //     // Clone materiaal per mesh
    //     const src = mesh.material;
    //     const mat = (mesh.material = src.clone());
    //     // Zorg dat we een materiaal gebruiken met emissive-ondersteuning
    //     if (!mat.emissive) {
    //         mesh.material = new THREE.MeshStandardMaterial({ color: 0xffffff });
    //     }
    //     const material = mesh.material;
    //     // Basis emissive kleur instellen (tint blijft constant)
    //     material.emissive.set(glowColor);
    //     // Niet-lineaire schaal: kies 2.0 of 3.0 voor sterker verschil
    //     const t = Math.min(Math.max(refine, 1), 9) / 9; // [0..1]
    //     material.emissiveIntensity = 0.05 * t;
    //     // Transparantie en opacity
    //     material.transparent = true;
    //     const opacity = Math.max(1 - refine * 0.015, 0.1);
    //     material.opacity = opacity;
    //     // Additive blending: temperen om oververzadiging te voorkomen
    //     material.blending = THREE.NormalBlending;
    //     material.depthWrite = false;
    //     const glowMat = new THREE.MeshBasicMaterial({
    //         color: glowColor,
    //         transparent: true,
    //         blending: THREE.AdditiveBlending,
    //         depthWrite: false,
    //     });
    //     const curve = Math.pow(t, 3.0);
    //     glowMat.opacity = 0.005 * curve; // 0 → 0.02 max; +1 ~ 2.7e-6, +9 ~ 0.02
    //     if (!mesh._glowShell) {
    //         const glow = new THREE.Mesh(mesh.geometry, glowMat);
    //         glow.matrixAutoUpdate = false;
    //         // glow.applyMatrix4(mesh.matrixWorld);
    //         mesh.add(glow);
    //         mesh._glowShell = glow;
    //     } else {
    //         mesh._glowShell.material = glowMat;
    //     }
    //     console.log(
    //         `Refine ${refine} → t=${t.toFixed(2)} curve=${curve.toFixed(
    //             3
    //         )} emissiveIntensity=${material.emissiveIntensity.toFixed(
    //             3
    //         )} opacity=${opacity.toFixed(3)}`
    //     );
    //
    // }

    // applyRefineGlow(
    //     mesh,
    //     refine,
    //     glowColor = 0x191600
    // ) {
    //     if (refine > 0) {
    //         // Clone zodat dit object een eigen materiaal krijgt
    //         const mat = (mesh.material = mesh.material.clone());
    //         // Zorg dat materiaal emissive ondersteunt (bijv. MeshStandard/Phong/Lambert)
    //         if (!("emissive" in mat)) {
    //             // Optioneel: converteer naar MeshStandardMaterial als emissive ontbreekt
    //             mesh.material = new THREE.MeshStandardMaterial({ color: 0xffffff });
    //         }
    //         const material = mesh.material;
    //         // Intensiteitsschaal (max ~0.9 bij refine 9 met 0.1 schaal)
    //         const intensity = Math.min(refine * 0.1, 1.0);
    //         // Basis kleur naar THREE.Color
    //         const base = new THREE.Color(glowColor);
    //         // Emissive kleur = base * intensity
    //         const emissiveColor = base.clone().multiplyScalar(intensity);
    //         material.emissive = emissiveColor;
    //         // Transparantie en opacity
    //         material.transparent = true;
    //         const opacity = Math.max(1 - refine * 0.015, 0.1);
    //         material.opacity = opacity;
    //         // Optioneel: voor sterkere additive glow-illusie
    //         material.blending = THREE.AdditiveBlending;
    //         material.depthWrite = false; // kan sorting issues verminderen bij transparantie
    //         console.log(
    //             `Refinement ${refine}, intensity: ${intensity.toFixed(
    //                 3
    //             )}, opacity: ${opacity.toFixed(3)}, color: #${base.getHexString()}`
    //         );
    //     }
    //
    // }

    // Socketed gem's effect (LIST_JEMITEM col 21 -> LIST_EFFECT row ->
    // FILE_EFFECT .eft) on the one ZSC effect point named by the item's
    // "gem effect position" (as rose-offline-client does): mid-blade on
    // swords, point 0 on shields.
    _attachGemEffects(entry: any, model: any, partIdx: number) {
        const itemType = partIdx === VISEQUIPIDX.WEAPON ? ITEMTYPE.WEAPON
            : partIdx === VISEQUIPIDX.SHIELD ? ITEMTYPE.SHIELD : 0;
        if (!itemType) return;
        GDM.get("item_data", "list_effect", (itemData: any, listEffect: any) => {
            const attachEffect = itemData.getGemStats(entry.gem).attachEffect;
            // LIST_EFFECT cols 2-5 are per-position effects; the data uses
            // the same file for all four.
            const fileEffectIdx = attachEffect && Number(listEffect.item(attachEffect, 2));
            if (!fileEffectIdx) return;
            let point = model.effects[itemData.getGemEffectPosition(itemType, entry.id)];
            // Books, dolls, etc. have no ZSC effect points: glow at the
            // centre of the first mesh instead.
            if (!point && entry.partMeshes[0]) {
                const geometry = entry.partMeshes[0].geometry;
                if (!geometry.boundingBox) geometry.computeBoundingBox();
                point = { parent: 1, position: geometry.boundingBox.getCenter(new THREE.Vector3()) };
            }
            const mesh = point && entry.partMeshes[(point.parent || 1) - 1];
            if (!mesh) return;
            EffectManager.loadEffectByIdx(fileEffectIdx, (effect: any) => {
                // Part replaced while loading -- don't attach to the new model.
                if (!effect || this.modelParts[partIdx] !== entry) return;
                if (point.position) effect.rootObj.position.copy(point.position);
                if (point.rotation) effect.rootObj.quaternion.copy(point.rotation);
                mesh.add(effect.rootObj);
                effect.play();
                entry.gemEffects.push(effect);
            });
        });
    }

    setModelPart(partIdx: any, modelIdx: any, refine: any, callback?: any, gem: number = 0) {
        let partType: any;
        if (this.gender === 0) {
            partType = MAVTPARTTYPES[partIdx];
        } else {
            partType = FAVTPARTTYPES[partIdx];
        }
        this.pendingResources++;
        this.totalResources++;

        const loadPart = () => {
            GDM.get(partType.dataName, (partModelList: any) => {
                this._setModelPart(
                    partModelList,
                    partIdx,
                    modelIdx,
                    refine,
                    partType.boneIdx,
                    partType.dummyIdx,
                    () => {
                        this.pendingResources--;
                        this._checkReady();
                        if (callback) {
                            callback();
                        }
                    },
                    gem
                );
            });
        };

        if (partIdx === VISEQUIPIDX.WEAPON && modelIdx) {
            // Bare(0) columnIdx = weaponMotionType 0. TYPE_MOTION.STB was
            // authored expecting this, see LIST_WEAPON.STB "Motion Type"
            // column for the per-weapon value (WEAPON_DATA.MOTION_TYPE).
            // item_data isn't loaded yet for character-select previews
            // (LoginState never loads it, only GameState does), so this must
            // wait rather than assume with getNow.
            GDM.get("item_data", (itemData: any) => {
                const row = itemData && itemData.getData(ITEMTYPE.WEAPON, modelIdx);
                this.weaponMotionType = row ? (parseInt(row[WEAPON_DATA.MOTION_TYPE], 10) || 0) : 0;
                loadPart();
            });
        } else {
            if (partIdx === VISEQUIPIDX.WEAPON) {
                this.weaponMotionType = 0;
            }
            loadPart();
        }
    }

    _getMotionData(motionFileIdx: any, callback: any) {
        GDM.get(
            "char_motions",
            function (this: any, charMotions: any) {
                this._waitSkeleton(
                    function (this: any) {
                        const motionRow = charMotions.row(motionFileIdx);
                        let motionFile = motionRow[MOTION_TABLE.MALE_MOTION];
                        if (this.gender === 1) {
                            const femMotionFile =
                                motionRow[MOTION_TABLE.FEMALE_MOTION];
                            if (femMotionFile) {
                                motionFile = femMotionFile;
                            }
                        }
                        CharPawn.motionFileCache.get(motionFile, callback);
                    }.bind(this)
                );
            }.bind(this)
        );
    }

    playMotion(motionIdx: any, timeScale: any, loop: any, animCallback?: any) {
        const motionTypes = GDM.getNow("char_motiontypes");
        const motionFileIdx = motionTypes.item(
            motionIdx,
            this.weaponMotionType
        );
        super.playMotion(motionFileIdx, timeScale, loop, animCallback);
    }

    playAttackMotion(onFinish: any, onImpact?: () => void) {
        let motionIdx = AVTANI.ATTACK;
        const animNum = Math.floor(Math.random() * 3);
        if (animNum === 1) {
            motionIdx = AVTANI.ATTACK2;
        } else if (animNum === 2) {
            motionIdx = AVTANI.ATTACK3;
        }
        this.playMotion(motionIdx, 1, 1, (anim: any) => {
            const cooldownMs = this.owner.stats.getAttackCooldownMs?.() || 2000;
            // Play the swing at its recorded speed. Only accelerate a long clip
            // when it would overlap the next server-authorized hit.
            anim.timeScale = Math.max(1, anim.length * 1000 / cooldownMs);
            this.bindAttackImpact(anim, onImpact);
            anim.once("finish", onFinish);
        });
    }

    playBareHandsAttackMotion(onFinish: any, onImpact?: () => void) {
        const motionIdx = AVTANI.ATTACK; // TODO: Cant find the bare hands animation
        this.playMotion(motionIdx, 1, 1, (anim: any) => {
            const cooldownMs = this.owner.stats.getAttackCooldownMs?.() || 2000;
            anim.timeScale = Math.max(1, anim.length * 1000 / cooldownMs);
            this.bindAttackImpact(anim, onImpact);
            anim.once("finish", onFinish);
        });
    }

    private bindAttackImpact(anim: any, onImpact?: () => void) {
        if (!onImpact) return;

        // Avatar attack ZMOs mark the visible weapon impact with event 31.
        // Fall back to the start of the motion for older assets without that
        // marker so an attack can never become input-only with no request.
        if (!anim.data?.eventFrames?.includes(31)) {
            onImpact();
            return;
        }

        const handleFrameEvent = (eventCode: number) => {
            if (eventCode !== 31) return;
            anim.removeEventListener("frame_event", handleFrameEvent);
            onImpact();
        };
        anim.on("frame_event", handleFrameEvent);
    }

    playIdleMotion() {
        if (this.owner?.isDriving) {
            this.playDrivingMotion(false);
            return;
        }
        this.playMotion(AVTANI.STOP1, 1.0, 0);
    }

    playDieMotion(onFinish?: () => void) {
        this.playMotion(AVTANI.DIE, 1.0, 1, function (anim: any) {
            if (onFinish) {
                anim.once("finish", onFinish);
            }
        });
    }

    playRunMotion() {
        if (this.owner?.isDriving) {
            this.playDrivingMotion(true);
            return;
        }
        const timeScale = (this.owner.moveSpeed + 180) / 600;
        this.playMotion(AVTANI.RUN, timeScale, 0);
    }

    playSitDownMotion(onFinish: any) {
        this.playMotion(AVTANI.SITTING, 1.0, 1, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    playSittingMotion() {
        this.playMotion(AVTANI.SIT, 1.0, 0);
    }

    playRaiseMotion(onFinish: any) {
        this.playMotion(AVTANI.RAISE, 1.0, 0, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    playPickupMotion(onFinish: any) {
        this.playMotion(AVTANI.PICKITEM, 1.0, 1, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    playJump1Motion(onFinish: any, speed = 1.0, loop = 1) {
        this.playMotion(AVTANI.JUMP1, speed, loop, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    playJump2Motion(onFinish: any, speed = 1.0, loop = 1) {
        this.playMotion(AVTANI.JUMP2, speed, loop, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    playStandUpMotion(onFinish: any, speed = 1.0, loop = 1) {
        this.playMotion(AVTANI.STANDUP, speed, loop, function (anim: any) {
            anim.once("finish", onFinish);
        });
    }

    newDamage(amount: any) {
        DamageRender.add(
            amount,
            this.avatarRoot.localToWorld(new THREE.Vector3(0, 0, 2.4))
        );
    }

    // PAT parts (cart frame/engine/wheel, or castle-gear weapon) are rigid
    // props attached to the character root -- not skeleton-bound armor, see
    // Task 14 of the driving/PAT implementation plan for why this doesn't
    // reuse setModelPart/_setModelPart. "itm_pat" is a ModelListManager
    // (registered against 3DDATA/PAT/LIST_PAT.ZSC in registrations.ts) with
    // itemNo indexing directly into its models[] array, exactly like
    // itm_weapon/itm_back -- LIST_PAT.STB's own "model path" STB column
    // (a `.txt` file) turned out to not exist anywhere in this asset set,
    // so it's not used here at all.
    patParts: THREE.Object3D[] = [];
    // Bumped by every setPatParts()/clearPatParts() call. Both GDM.get()
    // (outer) and ModelListManager._createMesh() (inner, per-part) are
    // async, and their callbacks close over `this` and mutate `this.
    // patParts`/`this.rootObj` whenever they eventually fire -- if the
    // player toggles driving off (clearPatParts, resetting patParts = [])
    // before a slow/first-load callback resolves, that stale callback would
    // otherwise still push a mesh into the (already-cleared) array and
    // attach it to rootObj with nothing left to ever remove it: a
    // permanently-attached ghost mesh on a character no longer driving, no
    // error. Every callback captures the generation at call time and bails
    // if a later setPatParts/clearPatParts has since superseded it.
    private _patGen = 0;

    setPatParts(itemNos: number[], vehicleKind: PatVehicleKind = null) {
        const wasMoving = this.patMoving;
        this.clearPatParts();
        this.patMoving = wasMoving;
        this.isCastleGearDriving = vehicleKind === 'castleGear';
        this.patVehicleKind = vehicleKind;
        const myGen = this._patGen;
        // "itm_pat" isn't preloaded anywhere (unlike itm_weapon/itm_back,
        // which setModelPart's normal equip flow always GDM.get()s during
        // character setup) -- nothing ever triggers its load before this
        // point since PAT parts aren't part of visParts. GDM.getNow()
        // throws "unloaded data resource" here (found 2026-09-16, first
        // live toggle_drive test); GDM.get() loads it on demand instead.
        // Reported once per itemNo per setPatParts() call -- a single missing
        // part typically fails both its mesh and its texture, and can recur
        // across several ModelPart entries; dedupe so one broken PAT item
        // produces one console warning, not a burst of identical ones.
        const reportedMissingResource = new Set<number>();
        const reportMissingResource = (itemNo: number) => {
            if (reportedMissingResource.has(itemNo)) return;
            reportedMissingResource.add(itemNo);
            GDM.get("item_data", (itemData: any) => {
                const name = itemData?.getName?.(ITEMTYPE.RIDE_PART, itemNo);
                console.warn(`${name || `PAT item #${itemNo}`} is implemented but its 3D resource is missing.`);
            });
        };
        const loadParts = (patData: any, gearSkeletonData?: any) => {
            if (myGen !== this._patGen) return; // superseded, discard
            if (!patData) return;
            if (gearSkeletonData) {
                this.patSkeleton = gearSkeletonData.create(this.patRoot);
                this.playPatMotion(this.patMoving);
            }
            for (const itemNo of itemNos) {
                if (!itemNo) continue;
                const model = patData.data.models[PAT_VISUAL_FALLBACK[itemNo] || itemNo];
                if (!model) continue;
                for (const part of model.parts) {
                    const material = patData._createMaterial(part.materialIdx, () => reportMissingResource(itemNo));
                    patData._createMesh(part.meshIdx, (geometry: any) => {
                        if (myGen !== this._patGen) return; // superseded, discard
                        const mesh = this.patSkeleton && geometry.getAttribute('skinIndex')
                            ? new THREE.SkinnedMesh(geometry, material)
                            : new THREE.Mesh(geometry, material);
                        if (part.position) mesh.position.copy(part.position);
                        if (part.rotation) mesh.quaternion.copy(part.rotation);
                        if (part.scale) mesh.scale.copy(part.scale);
                        if (mesh instanceof THREE.SkinnedMesh) mesh.bind(this.patSkeleton!);
                        this.patRoot.add(mesh);
                        this.patParts.push(mesh);
                    }, () => {
                        if (myGen !== this._patGen) return; // superseded, discard
                        reportMissingResource(itemNo);
                    });
                }
            }
        };
        const skel = vehicleKind ? PAT_VEHICLE_SKELETONS[vehicleKind] : null;
        if (skel) {
            // Request both resources together so a cold first mount does not
            // wait for the skeleton before it even starts loading PAT models.
            GDM.get(skel.skelName, "itm_pat", (skeletonData: any, patData: any) => {
                if (myGen === this._patGen) loadParts(patData, skeletonData);
            });
        } else {
            GDM.get("itm_pat", (patData: any) => loadParts(patData));
        }
    }

    clearPatParts() {
        this._patGen++;
        this.patMotionRequest++;
        this.patAnimation?.stop();
        this.patAnimation = null;
        this.patAnimationKind = null;
        this.patSkeleton = null;
        this.patRoot.clear();
        this.patParts = [];
        this.patMoving = false;
        this.isCastleGearDriving = false;
        this.patVehicleKind = null;
    }

    setDrivingHeight(driving: boolean, vehicleKind: PatVehicleKind = null) {
        this.isCastleGearDriving = driving && vehicleKind === 'castleGear';
        // Character movement treats local -Y as forward; keep the PAT meshes
        // grounded and shift only the rider in the vehicle's facing direction.
        this.avatarRoot.position.y = driving ? VEHICLE_RIDER_FORWARD_OFFSET : 0;
        this.avatarRoot.position.z = driving
            ? (vehicleKind === 'castleGear' ? CASTLE_GEAR_AVATAR_HEIGHT : DRIVING_AVATAR_HEIGHT)
            : 0;
    }

    playDrivingMotion(moving = false) {
        // The rider stays seated; the vehicle's own skeleton (Castle Gear or
        // Cart) plays its stop/walk clips for the machine itself.
        this.playMotion(AVTANI.SIT, 1.0, 0);
        this.patMoving = moving;
        this.playPatMotion(moving);
    }

    private playPatMotion(moving: boolean) {
        if (!this.patVehicleKind || !this.patSkeleton) return;
        const kind = moving ? 'walk' : 'stop';
        if (this.patAnimationKind === kind) return;
        this.patAnimationKind = kind;
        this.patAnimation?.stop();
        this.patAnimation = null;
        const request = ++this.patMotionRequest;
        const generation = this._patGen;
        const path = moving
            ? PAT_VEHICLE_SKELETONS[this.patVehicleKind].walk
            : PAT_VEHICLE_SKELETONS[this.patVehicleKind].stop;
        CharPawn.motionFileCache.get(path, (animationData: AnimationData) => {
            if (generation !== this._patGen || request !== this.patMotionRequest || !this.patSkeleton) return;
            const animation = new SkeletonAnimator(this.patSkeleton, animationData);
            animation.loop = 0;
            animation.play();
            this.patAnimation = animation;
        });
    }
}

const GENDERSKELNAMES = {
    0: "male_skel",
    1: "female_skel",
};

const MAVTPARTTYPES = [
    { dataName: "itm_mface", boneIdx: 4 },
    { dataName: "itm_mhair", boneIdx: 4 },
    { dataName: "itm_mcap", dummyIdx: DummyIndex.Cap },
    { dataName: "itm_mbody" },
    { dataName: "itm_marms" },
    { dataName: "itm_mfoot" },
    { dataName: "itm_mfaceitem", dummyIdx: DummyIndex.Mouse },
    { dataName: "itm_back", dummyIdx: DummyIndex.Back },
    { dataName: "itm_weapon" },
    { dataName: "itm_subwpn" },
];
const FAVTPARTTYPES = [
    { dataName: "itm_fface", boneIdx: 4 },
    { dataName: "itm_fhair", boneIdx: 4 },
    { dataName: "itm_fcap", dummyIdx: DummyIndex.Cap },
    { dataName: "itm_fbody" },
    { dataName: "itm_farms" },
    { dataName: "itm_ffoot" },
    { dataName: "itm_ffaceitem", dummyIdx: DummyIndex.Mouse },
    { dataName: "itm_back", dummyIdx: DummyIndex.Back },
    { dataName: "itm_weapon" },
    { dataName: "itm_subwpn" },
];

/**
 * @constructor
 *
 * @property {Object.<string,SkeletonAnimator>} motionCache
 * Holds a cache of all animations that are actually loaded against
 * this skeleton and can be used immediately (and should not be loaded
 * against the skeleton again).
 */

/**
 * Holds a cache of all loaded animation files data.  This is just the data,
 * and not bound to any particular skeleton.
 *
 * @type {DataCache.<AnimationData>}
 */
CharPawn.motionFileCache = new DataCache(AnimationData);

//
